import lodash, { cloneDeep } from 'lodash';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import Streaming from '../../../../services/_realtime/price/Streaming';
import { Button, Col, Container, Row, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRotateRight, faGear, faGripDotsVertical, faSquareCheck, faSquareChevronRight, faStar, faSort, faSortDown, faSortUp, faChartLine, faSearch, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import '@grapecity/wijmo.styles/wijmo.css';
import { FlexGrid, FlexGridColumn } from '@grapecity/wijmo.react.grid';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import './style.scss';
import { useTranslation } from "react-i18next";
import { Toast } from 'primereact/toast';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import Search from '../../../../components/search/search';
import { DashboardServiceApi } from '../../../../services/watchlist/api';
import { Configuration } from '../../../../services/watchlist/configuration';
import { ThemeContext } from '../../../../context/theme-context';
import ScoreFields from '../../../../components/score/scoreFields';
import { useDispatch, useSelector } from 'react-redux';
import { setUpdatedList } from '../../../../stores/watchlistTableSearchItemSlice';
import { Tooltip } from 'primereact/tooltip';
import Spinner from '../../../../components/spinner';
import NumberFormatter from '../../../../formatters/NumberFormatter';
import moment from 'moment';

const formatCellValue = (value) => {
    if (value === undefined || value === null || value === '') {
        return '-';
    }
    return value;
};

const WL_PERF_ENABLED = typeof window !== 'undefined' && window.location.search.includes('wlPerf=1');

const measureWatchlistPerf = () => {
    if (!WL_PERF_ENABLED || typeof performance === 'undefined' || !performance.mark || !performance.measure) return;

    const startEntries = performance.getEntriesByName('wl:title-click:start');
    if (!startEntries || startEntries.length === 0) return;

    const endMarkName = 'wl:title-click:end';
    const measureName = 'wl:title-click:table-ready';

    performance.mark(endMarkName);
    performance.measure(measureName, 'wl:title-click:start', endMarkName);

    const measures = performance.getEntriesByName(measureName);
    const last = measures[measures.length - 1];
    if (last) {
        // eslint-disable-next-line no-console
        console.log(`[WL PERF] ${measureName}: ${last.duration.toFixed(1)} ms`);
    }

    performance.clearMarks('wl:title-click:start');
    performance.clearMarks(endMarkName);
    performance.clearMeasures(measureName);
};

function WatchlistTable({ refreshListAndTable, symbolListData, removeSymbolOnWatchlist, columnsList, tableData, removedEntityId, selectedListBarItem, staticLiveData, activeCategory, onItemsFiltered }) {
    const watchlistApi = useMemo(() => new DashboardServiceApi(new Configuration({ basePath: process.env.REACT_APP_WATCHLIST_API_BASE_PATH })), []);

    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const currentTheme = useContext(ThemeContext).theme;
    const token = localStorage.getItem('token');

    // Refs
    let streamingRef = useRef(null);
    const unsubscribeLiveStreaming = useRef(null);
    const toast = useRef(null);
    const cellElements = useRef({});
    const columnsDataRef = useRef([]);
    const flex = useRef(null);
    const theGridRef = useRef(null);
    const selectedRatioCategoryRef = useRef('Live');
    const selectedListBarItemRef = useRef(selectedListBarItem);
    const reactCellRootsRef = useRef(new Map());
    const reactCellCacheKeyRef = useRef(new Map());
    const indicatorCellStateRef = useRef(new WeakMap());
    const measuredCategoryIdRef = useRef(null);
    const renderStartTimeRef = useRef(null);
    const renderedCategoryIdRef = useRef(null);
    // Track one-time grid initialization to prevent full re-init on every dependent change
    const gridInitializedRef = useRef(false);

    const [columns, setColumns] = useState([]);
    const [columnsData, setColumnsData] = useState([]);
    const [count, setCount] = useState(0);
    const [emptyList, setEmptyList] = useState(false);
    const [gridRows, setGridRows] = useState([]);
    const [initialColumnsList, setInitialColumnsList] = useState([]);
    const [isSearchDisabled, setIsSearchDisabled] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [streamLoading, setStreamLoading] = useState(true);
    const [theme, setTheme] = useState('light');
    const [visibleColumnSelect, setVisibleColumnSelect] = useState(false);

    const numberFormatter = new NumberFormatter();

    const activeListBarItem = useSelector((state) => state.watchlist.activeListBarItem);
    const activeTitle = useSelector((state) => state.watchlist.activeTitle);
    const activeTitleId = useSelector((state) => state.watchlist.activeTitleId);
    const JustAddedSymbol = useSelector((state) => state.watchlist.JustAddedSymbol);
    const fixedColumns = React.useMemo(() => [
        { binding: "favWatchlist", header: " ", width: 25, align: "center", dataType: "String", visible: true, isRealtimeContent: false, canbeHide: false, visibleIndex: 0, isReadOnly: true, isContentHtml: true, wordWrap: false, allowSorting: false, cssClass: 'fixed-columns-bg', isVisible: true, defaultVisible: true },
        { binding: "UniqueCode", header: t('tableHeader.securityCode'), width: 55, align: "left", dataType: "String", visible: true, isRealtimeContent: false, canbeHide: false, visibleIndex: 1, isReadOnly: true, isContentHtml: true, wordWrap: false, allowSorting: true, cssClass: 'fixed-columns-bg', isVisible: true, defaultVisible: true },
        { binding: "name", header: t('tableHeader.securityName'), width: 125, align: "left", dataType: "String", visible: true, isRealtimeContent: false, canbeHide: false, visibleIndex: 1, isReadOnly: true, isContentHtml: true, wordWrap: false, allowSorting: true, cssClass: 'fixed-columns-bg', isVisible: true, defaultVisible: true },
        { binding: "technicalAnalysis", header: " ", width: 34, align: "left", dataType: "String", visible: true, isRealtimeContent: false, canbeHide: false, visibleIndex: 1, isReadOnly: true, isContentHtml: true, wordWrap: false, allowSorting: false, cssClass: 'fixed-columns-bg', isVisible: true, defaultVisible: true },
        { binding: "indicators", header: t('tableHeader.indicators'), width: 145, align: "left", headerAlign: "center", dataType: "String", visible: true, isRealtimeContent: false, canbeHide: false, visibleIndex: 2, isReadOnly: true, isContentHtml: true, wordWrap: false, allowSorting: false, cssClass: 'fixed-columns-bg-last', isVisible: true, defaultVisible: true }
    ], [t]);
    const alwaysCounted = ['UniqueCode', 'name', 'indicators'];
    const getVisibleCount = cols => alwaysCounted.length + cols.filter(c => c.isVisible && !fixedColumns.some(f => f.binding === c.binding)).length;

    const arraysEqual = (a, b) => {
        if (!a || !b || a.length !== b.length) return false;
        for (let i = 0; i < a.length; i++) {
            if (a[i] !== b[i]) return false;
        }
        return true;
    };

    const getOrderStorageKey = () => {
        const listId = selectedListBarItemRef.current || selectedListBarItem;
        const categoryBinding = (selectedRatioCategoryRef.current || activeCategory?.binding || '').toLowerCase();
        if (!listId || !categoryBinding) return null;
        return `watchlist-column-order:${listId}:${categoryBinding}`;
    };

    const persistOrderLocally = (bindings) => {
        const key = getOrderStorageKey();
        if (!key || !bindings?.length) return;
        try {
            localStorage.setItem(key, JSON.stringify(bindings));
        } catch (err) {
            console.warn('Failed to persist column order', err);
        }
    };

    const readStoredOrder = () => {
        const key = getOrderStorageKey();
        if (!key) return null;
        try {
            const raw = localStorage.getItem(key);
            return raw ? JSON.parse(raw) : null;
        } catch (err) {
            console.warn('Failed to read column order', err);
            return null;
        }
    };

    const applyStoredOrder = (allCols) => {
        const storedOrder = readStoredOrder();
        if (!storedOrder || storedOrder.length === 0) return allCols;

        const fixedBindings = new Set(fixedColumns.map(col => col.binding));
        const fixed = allCols.filter(col => fixedBindings.has(col.binding));
        const dynamics = allCols.filter(col => !fixedBindings.has(col.binding));
        const dynamicMap = new Map(dynamics.map(col => [col.binding, col]));
        const orderedDynamics = [];

        storedOrder.forEach(binding => {
            const col = dynamicMap.get(binding);
            if (col) {
                orderedDynamics.push(col);
                dynamicMap.delete(binding);
            }
        });

        // Append any new columns that don't exist in the stored order
        dynamicMap.forEach(col => orderedDynamics.push(col));

        const orderedWithIndexes = orderedDynamics.map((col, idx) => ({ ...col, order: idx + 1 }));

        return [...fixed, ...orderedWithIndexes];
    };

    const getGridDynamicBindings = () => {
        const grid = theGridRef.current?.control || flex.current;
        if (!grid) return [];
        const fixedBindings = new Set(fixedColumns.map(c => c.binding));
        return grid.columns.map(col => col.binding).filter(binding => binding && !fixedBindings.has(binding));
    };

    const syncStateWithBindingOrder = (bindingOrder) => {
        if (!bindingOrder || bindingOrder.length === 0) return;
        const fixedBindings = new Set(fixedColumns.map(col => col.binding));
        const currentDynamics = columnsDataRef.current.filter(col => !fixedBindings.has(col.binding));
        const dynamicMap = new Map(currentDynamics.map(col => [col.binding, col]));
        const orderedDynamics = [];

        bindingOrder.forEach((binding, idx) => {
            const col = dynamicMap.get(binding);
            if (col) {
                orderedDynamics.push({ ...col, order: idx + 1 });
                dynamicMap.delete(binding);
            }
        });

        dynamicMap.forEach(col => {
            orderedDynamics.push({ ...col, order: orderedDynamics.length + 1 });
        });

        const merged = [...fixedColumns, ...orderedDynamics];
        setColumnsData(merged);
        setColumns(orderedDynamics);
        setInitialColumnsList(cloneDeep(merged));
    };


    useEffect(() => {

        if (activeListBarItem) {
            if (activeListBarItem.count <= 0) {
                setEmptyList(true);
            } else {
                setEmptyList(false);
            }

        }
    }, [activeListBarItem]);




    useEffect(() => {
        if (selectedListBarItem) {
            const searchElement = document.querySelector('.search-container-classname');
            if (searchElement) {
                if (selectedListBarItem.includes('xu030') || selectedListBarItem.includes('xu100')) {
                    searchElement.style.opacity = 0;
                    searchElement.style.cursor = 'not-allowed';
                    setIsSearchDisabled(true);
                } else {
                    searchElement.style.opacity = 1;
                    setIsSearchDisabled(false);
                }
            }
            // //invalidateGrid();
        }
    }, [selectedListBarItem]);

    useEffect(() => {
        selectedListBarItemRef.current = selectedListBarItem;
    }, [selectedListBarItem]);



    useEffect(() => {
        if (columnsList.length === 0 || !columnsList) return;

        const sorted = lodash.sortBy(columnsList, 'order');
        // Filter out any fixed column bindings from dynamic columns
        const fixedBindings = fixedColumns.map(col => col.binding);
        const dynamics = sorted.filter(c => !fixedBindings.includes(c.binding));

        // Create a new array preserving our memoized fixedColumns reference and apply stored order if available
        const allCols = [...fixedColumns, ...dynamics];
        const orderedCols = applyStoredOrder(allCols);
        const orderedDynamics = orderedCols.filter(c => !fixedBindings.includes(c.binding));

        setColumnsData(orderedCols);
        setInitialColumnsList(cloneDeep(orderedCols));
        setColumns(orderedDynamics);
        setCount(getVisibleCount(orderedCols));

        if (flex.current) {
            // Only apply info to dynamic columns to avoid re-rendering fixed columns
            orderedDynamics.forEach(c => {
                if (!c.info) return;
                const gCol = flex.current.columns.getColumn(c.binding);
                if (gCol) gCol.info = c.info;
            });
        }
    }, [columnsList, fixedColumns]);

    useEffect(() => {
        columnsDataRef.current = columnsData;
    }, [columnsData]);


    const setupRow = (row) => {
        row.pricePrecision = 2;
        row.HistoryRepository = {};
        row.CountryCode = row.Flag === undefined ? row.CountryCode : row.Flag;
        for (var k = 0; k < columnsData.length; k++) {
            row.HistoryRepository[columnsData[k].binding] = [];
        }
    }

    const mergeStaticLiveData = (filteredData) => {
        if (staticLiveData.length > 0) {
            const indicatorsByEntity = new Map(staticLiveData.map(item => [item.entityId, item.indicators]));
            for (let index = 0; index < filteredData.length; index++) {
                const element = filteredData[index];
                const indicators = indicatorsByEntity.get(element.entityId);
                if (indicators) {
                    element.indicators = indicators;
                }
            }
        }
        return filteredData;
    };

    const resetStreaming = () => {
        if (streamingRef.current) {
            streamingRef.current = null;
            unsubscribeLiveStreaming.current = null;
        }
    };

    // Reset streaming and restart processing when symbol list changes
    useEffect(() => {

        if (symbolListData.length > 0) {
            resetStreaming();
            setStreamLoading(true); // Yeni data gelirken loading açık kalsın
        }
        else {
            setGridRows([]);
            setStreamLoading(false);
        }
    }, [symbolListData]);

    const filterData = (data, term) => {
        return term ? data.filter(item =>
            lodash.some(item, value => typeof value === "string" && value.toLowerCase().includes(term.toLowerCase()))
        ) : data;
    };

    const setupData = (data) => {
        let objectIds = [];

        // Filter data if activeCategory id is 4
        let filteredData = data;
        let itemsWereFiltered = false;

        if (activeCategory && activeCategory.id === 4) {
            filteredData = data.filter(item => {
                if (!item.HistoryRepository) {
                    item.HistoryRepository = {};
                    columnsData.forEach(col => {
                        item.HistoryRepository[col.binding] = [];
                    });
                }

                // Check if row has any non-empty values in relevant columns
                return Object.keys(item).some(key => {
                    // Skip metadata fields and only check actual data columns
                    if (['entityId', 'code', 'name', 'indicators', 'HistoryRepository', 'CountryCode', 'Flag', 'pricePrecision'].includes(key)) {
                        return false;
                    }

                    // Include row if it has at least one non-empty value
                    const value = item[key];
                    return value !== null && value !== undefined && value !== '';
                });
            });
            // itemsWereFiltered = data.length > filteredData.length;
            itemsWereFiltered = false;
        }

        if (onItemsFiltered) {
            onItemsFiltered(itemsWereFiltered);
        }

        // Continue with the filtered data
        filteredData.forEach(item => {
            setupRow(item);
            objectIds.push(item.entityId);
        });
        setGridRows([...filteredData]); // <-- CLONE ARRAY TO FORCE RE-RENDER

        return objectIds;
    };

    const initializeStreaming = (objectIds) => {
        if (!streamingRef.current) {
            streamingRef.current = new Streaming();
            unsubscribeLiveStreaming.current = streamingRef.current.subscribe({ objectIds }, (data) => {
                processStreamingData(data);
            }, handleError);
        }
    };

    const handleError = (error) => {
        console.error('Streaming error:', error);
        // Optionally, show a toast or notification to the user
    };
    const debounceTimeout = useRef(null);
    useEffect(() => {

        //
        const doSetup = (data) => {
            const start = performance.now();
            const filtered = mergeStaticLiveData(filterData(data, searchTerm));

            // Now setupData will further filter based on activeCategory.id === 4
            const objectIds = setupData(filtered);
            const end = performance.now();
            console.log(`[watchlist log] 📊 Veri hazırlığı (doSetup) tamamlandı: ${(end - start).toFixed(2)} ms | Satır (ObjectID): ${objectIds.length}`);
            
            // İşlem bitince hangi kategori render edildiyse onu işaretliyoruz ki spinner kapanma kararı buna baksın
            renderedCategoryIdRef.current = activeCategory?.id;
            
            return objectIds;
        };

        if (!tableData || tableData.length === 0) {
            resetStreaming();
            const objectIds = doSetup(symbolListData);
            initializeStreaming(objectIds);
            return () => unsubscribeLiveStreaming.current?.Dispose();
        }

        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
        // debounceTimeout.current = setTimeout(() => {
        //     doSetup(tableData);
        // }, 600);
        doSetup(tableData);

        return () => clearTimeout(debounceTimeout.current);
    }, [tableData, symbolListData, searchTerm, activeCategory?.id]); // Only depend on critical changes

    useEffect(() => {
        if (removedEntityId === "") return;
        let newGridRows = gridRows.filter((item) => item.entityId !== removedEntityId);
        setGridRows(newGridRows);

    }, [removedEntityId]);

    useEffect(() => {
        setTheme(currentTheme);
    }, [currentTheme]);

    useEffect(() => {
        if (activeCategory?.binding) {
            selectedRatioCategoryRef.current = activeCategory.binding;
        }
    }, [activeCategory?.binding]);

    const isLiveCategory = () => (selectedRatioCategoryRef.current || '').toLowerCase() === 'live';
    const mapCategoryBinding = (binding) => {
        const normalized = (binding || '').toLowerCase();
        const mapping = {
            performance: 'performance',
            valuation: 'valuation',
            analysisrecommendations: 'analysisrecommendations',
            technicalanalysis: 'technicalanalysis',
        };
        return mapping[normalized] || normalized;
    };

    const clearReactCellRoots = () => {
        reactCellRootsRef.current.forEach(entry => entry.root.unmount());
        reactCellRootsRef.current.clear();
        reactCellCacheKeyRef.current.clear();
        indicatorCellStateRef.current = new WeakMap();
    };

    const renderReactCell = (cell, colBinding, entityId, cacheKey, element) => {
        const uniqueKey = `${colBinding}-${entityId}`;
        let entry = reactCellRootsRef.current.get(uniqueKey);

        if (!entry) {
            const container = document.createElement('div');
            container.setAttribute('data-react-cell', 'true');
            // Inline stiller kaldırıldı - FlexGrid kendi sınıf ve hizalamalarını uygulasın
            const root = createRoot(container);
            entry = { container, root, cacheKey: null };
            reactCellRootsRef.current.set(uniqueKey, entry);
        }

        // Grid geri dönüşümünde DOM elementini sadece eşleştir.
        if (cell.firstElementChild !== entry.container) {
            cell.innerHTML = '';
            cell.appendChild(entry.container);
        }

        // Eğer veri/parametre aynıysa render etmeye gerek yok, DOM reparenting işlemi zaten senkron tamamlandı!
        if (entry.cacheKey === cacheKey) {
            return;
        }

        entry.root.render(element);
        entry.cacheKey = cacheKey;
    };

    useEffect(() => {
        return () => {
            unsubscribeLiveStreaming.current?.Dispose?.();
            clearReactCellRoots();
        };
    }, []);

    // Kategori değiştiğinde loading aç
    useEffect(() => {
        if (activeCategory?.id) {
            renderStartTimeRef.current = performance.now();
            console.log(`[watchlist log] ⌛ Kategori değişti (${activeCategory?.id}). Tablo gizlendi, yükleme başlıyor. Timestamp: ${renderStartTimeRef.current.toFixed(2)} ms`);
            setStreamLoading(true);
            measuredCategoryIdRef.current = null;
        }
    }, [activeCategory?.id]);

    // One-time grid initialization
    useEffect(() => {
        if (theGridRef.current && theGridRef.current.control && !gridInitializedRef.current) {
            initializeGrid(theGridRef.current.control);
            gridInitializedRef.current = true;
        }
    }, []);

    // Lightweight refresh on theme or category changes without destroying handlers
    useEffect(() => {
        if (gridInitializedRef.current && flex.current) {
            // Batch invalidation for minimal repaint
            flex.current.beginUpdate();
            flex.current.invalidate();
            flex.current.endUpdate();
        }
    }, [theme, activeCategory?.id]);

    // Loading'i grid+data tamamen hazır olunca kapat
    useEffect(() => {
        if (gridInitializedRef.current && gridRows.length > 0 && renderedCategoryIdRef.current === activeCategory?.id) {
            // Grid initialize ve rows dolu - loading'i güvenli şekilde kapat
            const timer = setTimeout(() => {
                const endTime = performance.now();
                const totalTime = renderStartTimeRef.current ? (endTime - renderStartTimeRef.current).toFixed(2) : 'N/A';
                console.log(`[watchlist log] ✅ Tablo render tamamlandı. Kategori: ${activeCategory?.id} | Toplam Süre: ${totalTime} ms | Satır Sayısı: ${gridRows.length}`);
                setStreamLoading(false);
            }, 350);
            return () => clearTimeout(timer);
        }
        
        // Fallback: Eğer 5 saniye sonra hala loading görünüyorsa zorla kapat
        if (streamLoading) {
            const fallbackTimer = setTimeout(() => {
                const endTime = performance.now();
                const totalTime = renderStartTimeRef.current ? (endTime - renderStartTimeRef.current).toFixed(2) : 'N/A';
                console.log(`[watchlist log] ⚠️ Tablo render timeout (fallback). Kategori: ${activeCategory?.id} | Toplam Süre: ${totalTime} ms | Satır Sayısı: ${gridRows.length}`);
                setStreamLoading(false);
            }, 5000);
            return () => clearTimeout(fallbackTimer);
        }
    }, [gridRows, tableData, streamLoading, activeCategory?.id]);

    useEffect(() => {
        if (!streamLoading && activeCategory?.id && measuredCategoryIdRef.current !== activeCategory.id) {
            measuredCategoryIdRef.current = activeCategory.id;
            measureWatchlistPerf();
        }
    }, [streamLoading, activeCategory?.id]);



    const processStreamingData = (msg) => {
        {
            const objectId = msg['@'];
            // Loading'i burada kapatma - grid tam hazır olana kadar açık kalsın
            if (!objectId || objectId === 'heartbeat') return;
            const grid = flex.current;
            if (!grid) return;
            const row = grid.rows.find(r => r.dataItem.entityId === objectId);
            if (!row) return;
            Object.keys(msg).forEach(key => {
                if (key === '@') return;
                const col = grid.columns.getColumn(key);
                if (!col) return;

                // 1) History’e önceki değeri ekle
                const hist = row.dataItem.HistoryRepository[key] || [];
                hist.push(row.dataItem[key]);
                if (hist.length > 2) hist.shift();
                row.dataItem.HistoryRepository[key] = hist;

                // 2) Yeni değeri ata
                row.dataItem[key] = msg[key];

                // 3) Render et
                const cell = cellElements.current[objectId]?.[key];
                if (cell) formatCell(cell, row.dataItem, col, true);
            });
        }
    }


    const initializeGrid = (flexgrid) => {
        // Guard against a disposed/detached grid. When switching custom lists
        // rapidly the FlexGrid can be unmounted before this callback runs, in
        // which case hostElement is null and any DOM access below would throw.
        if (!flexgrid || !flexgrid.hostElement) {
            return;
        }

        if (flexgrid.columnHeaders.rows.length > 0) {
            flexgrid.columnHeaders.rows[0].height = 76;
        }
        flexgrid.columnHeaders.rows.defaultSize = 30;
        flexgrid.rows.defaultSize = 45;
        flexgrid.deferResizing = true;

        columnsData.forEach(colData => {
            if (colData.info) {
                const gridCol = flexgrid.columns.getColumn(colData.binding);
                if (gridCol) {
                    gridCol.info = colData.info;
                }
            }
        });

        // Clear all existing formatItem handlers to prevent duplication
        flexgrid.formatItem.removeAllHandlers();
        clearReactCellRoots();
        
        // Enable batch updates for better performance
        flexgrid.beginUpdate();

        flex.current = flexgrid;
        flexgrid.formatItem.addHandler((s, e) => {
            if (e.panel === s.columnHeaders || e.panel === s.cells) {
                const column = s.columns[e.col];
                let alignment = column.align || 'left'; // Default alignment is left

                // Use headerAlign if available, otherwise use align
                const headerAlignment = column.headerAlign || alignment;

                // Set cell styles for both headers and cells
                e.cell.style.display = 'flex';
                e.cell.style.alignItems = 'center';

                e.cell.style.justifyContent = alignment === 'left' ? 'flex-start' : alignment === 'center' ? 'center' : 'flex-end';
                const gettheme = localStorage.getItem('default-theme');
                if (['favWatchlist', 'UniqueCode', 'name', 'technicalAnalysis', 'indicators'].includes(column.binding)) {
                    e.cell.style.backgroundColor = gettheme === 'dark' ? '#2C2C2C' : 'white';
                    e.cell.style.color = gettheme === 'dark' ? 'white' : 'black';
                    // e.cell.style.border = '1px solid #E0E0E0';
                }
                if (['indicators'].includes(column.binding)) {
                    // e.cell.style.borderRight = ".5px solid " + (gettheme === 'dark' ? '#626262' : '#e4e4e4'); // Only right border for indicators
                    // e.cell.style.boxShadow = gettheme === 'dark' ? '3px 0px 5px 0px #626262' : '3px 0px 5px 0px #e4e4e4'; // Adding box shadow for indicators with theme differentiation
                    // e.cell.style.borderLeft = 'none';

                }
                // Adjust header cell styles for wrapping and alignment
                if (e.panel === s.columnHeaders) {


                    e.cell.innerHTML = ''; // Clear existing content to prevent duplicate icons
                    e.cell.style.whiteSpace = 'normal';
                    e.cell.style.lineHeight = '1.2'; // Increase line height as needed
                    e.cell.style.overflow = 'hidden'; // Hide overflow text
                    e.cell.style.textOverflow = 'ellipsis'; // Add ellipsis for overflow text
                    e.cell.style.fontSize = '12px'; // Adjust font size as needed
                    e.cell.style.display = 'flex'; // Adjust font weight as needed
                    // e.cell.classList.add('table-header');
                    e.cell.style.justifyContent = 'center'; // Adjust font weight as needed
                    e.cell.style.alignItems = 'center'; // Adjust font weight as needed
                    // Add title text
                    const titleSpan = document.createElement('span');
                    titleSpan.textContent = column.header;
                    e.cell.appendChild(titleSpan);

                    if (s.collectionView && column.binding !== 'favWatchlist' && column.binding !== 'technicalAnalysis' && column.binding !== 'indicators') {
                        const sd = s.collectionView.sortDescriptions.find(sd => sd.property === column.binding);
                        const iconContainer = document.createElement('span');
                        iconContainer.style.marginLeft = '8px';
                        iconContainer.style.opacity = sd ? '1' : '0.3';
                        iconContainer.style.fontSize = '11px';
                        iconContainer.style.color = '#2fbc99'; // Main app color
                        iconContainer.innerHTML = sd ? (sd.ascending ? '<i class="fa-solid fa-sort-up"></i>' : '<i class="fa-solid fa-sort-down"></i>') : '<i class="fa-solid fa-sort"></i>';
                        e.cell.appendChild(iconContainer);
                    }

                    // Align the header based on headerAlignment
                    e.cell.style.justifyContent = headerAlignment === 'left' ? 'flex-start' : headerAlignment === 'center' ? 'center' : 'flex-end';
                    if (column.binding === 'indicators') {
                        e.cell.style.justifyContent = 'center';
                    }
                    // Check for column.info (now transferred)
                    if (column.info) {
                        const iconContainer = document.createElement('span');
                        iconContainer.style.marginLeft = '5px';
                        iconContainer.className = 'header-info-icon-container info-icon-header';
                        iconContainer.title = column.info;
                        iconContainer.innerHTML = '<i class="fa-solid fa-circle-info"></i>';
                        e.cell.appendChild(iconContainer);
                    }
                    // Align the header content
                    e.cell.style.display = 'flex';
                    e.cell.style.alignItems = 'center';
                }
            }

            if (e.panel === s.cells) {
                const col = s.columns[e.col];
                const ritem = s.rows[e.row].dataItem;
                if (!cellElements.current[ritem.entityId]) {
                    cellElements.current[ritem.entityId] = { item: ritem };
                }
                cellElements.current[ritem.entityId][col.binding] = e.cell;
                formatCell(e.cell, ritem, col, false);
            }

            // flexgrid.invalidate();
        });

        flexgrid.scrollPositionChanged.addHandler(() => {
            //invalidateGrid();
        });


        // Disable dragging for the first 5 columns
        flexgrid.columns.forEach((col, index) => {
            col.allowDragging = index >= 5;
            if (columnsData[index]) {
                col.width = columnsData[index].width;
            }

            // Set columns to be frozen
            if (index < 5) {
                col.frozen = true; // This makes the column fixed
            }
        });


        let resizeTimeout;
        flex.current = flexgrid;
        flexgrid.resizedColumn.addHandler(() => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                if (isLiveCategory()) {
                    syncRealtimeColumnsField(null);
                } else {
                    syncColumnsField(null);
                }
            }, 300); // Adjust debounce timeout as needed
        });

        flexgrid.draggedColumn.addHandler(() => {
            const fixedBindings = new Set(fixedColumns.map(col => col.binding));
            const bindingOrder = flexgrid.columns
                .map(col => col.binding)
                .filter(binding => binding && !fixedBindings.has(binding));

            const currentOrder = columnsDataRef.current
                .filter(col => !fixedBindings.has(col.binding))
                .map(col => col.binding);

            persistOrderLocally(bindingOrder);
            syncStateWithBindingOrder(bindingOrder);

            if (isLiveCategory()) {
                syncRealtimeColumnsField(null);
            } else {
                syncColumnsField(null);
            }
        });
        
        // End batch update
        flexgrid.endUpdate();
        
        // Clear all listener tracking attributes when grid reinitializes.
        // hostElement may have been detached mid-run if the list changed, so
        // re-check before touching the DOM.
        if (flexgrid.hostElement) {
            const allCells = flexgrid.hostElement.querySelectorAll('[data-has-listener]');
            allCells.forEach(cell => {
                delete cell.dataset.hasListener;
            });
        }
    };

    //#region Format Cells

    const formatCell = (cell, item, col, flare) => {
        switch (col.binding) {
            case 'bid':
                formatBidCell(cell, item, col, flare);
                break;
            case 'ask':
                formatAskCell(cell, item, col, flare);
                break;
            case 'dailyHigh':
                formatDailyHighCell(cell, item, col, flare);
                break;
            case 'dailyLow':
                formatDailyLowCell(cell, item, col, flare);
                break;
            case 'dailyAverage':
            case 'dailyChangeM':
            case 'dailyChangePercentageM':
            case 'dailyChangePrice':
            case 'dailyOpen':
            case 'dailyPrevClose':
            case 'lastPrice':
            case 'mCap':
                formatDynamicCell(cell, item, col, flare);

                break;
            case 'rocPrevYear':
            case 'rocPrevWeek':
            case 'rocPrevMonth':
            case 'rocEOFPrevYear':
                formatChangeCell(cell, item, col, flare);
                break;
            case 'dailyChange':
                formatDailyChangeCell(cell, item, col);
                break;
            case 'UniqueCode':
                formatSymbolCell(cell, item, col, flare);
                break;
            case 'name':
                formatSymbolNameCell(cell, item, col, flare);
                break;
            case 'lastUpdateTime':
                formatLastUpdateTimeCell(cell, item, col, flare);
                break;
            case 'lastTradeTime':
                formatLastTradeTimeCell(cell, item, col, flare);
                break;
            case 'marketCap':
                formatMarketCapCell(cell, item, col, flare);
                break;
            case 'securityCode':
                formatTickerCell(cell, item, col, flare);
                break;
            case 'favWatchlist':
                formatFavWatchlistCell(cell, item, col, flare);
                break;
            case 'technicalAnalysis':
                formatTechnicalAnalysisCell(cell, item, col, flare);
                break;
            case 'dailyVolume':
                formatDailyVolumeCell(cell, item, col, flare);
                break;
            case 'dailyValue':
                formatDailyValueCell(cell, item, col, flare);
                break;
            case 'indicators':
                formatIndicatorsCell(cell, item, col, flare);
                break;
            case 'signalArray':
                formatSignalArrayCell(cell, item, col, flare);
                break;
            case '562':
                formatAnalysisNumber(cell, item, col, flare);
                break;
            case '588':
                formatAnaysisAdvisor(cell, item, col, flare);
                break;
            case '589':
                formatAnaysisAdvisor(cell, item, col, flare);
                break;
            case '590':
                formatAnaysisAdvisor(cell, item, col, flare);
                break;
            default:
                cell.textContent = formatValueCell(item, col);
                break;
        }
    }

    const formatAnalysisNumber = (cell, item, col, flare) => {
        cell.innerHTML = formatCellValue(item[col.binding]);
    }

    const formatAnaysisAdvisor = (cell, item, col, flare) => {
        cell.innerHTML = formatCellValue(item[col.binding]);
    }


    // --- Custom formatBidCell as instructed ---
    const formatBidCell = (cell, item, col, flare) => {
        // Değeri formatla
        const cellValue = formatValueCell(item, col);

        // Tarihçe verisi yoksa temel stili göster
        if (!item["HistoryRepository"]) {
            cell.textContent = cellValue;
            return;
        }

        // Bu kolon için geçmiş verilerini al
        const history = item.HistoryRepository[col.binding] || [];

        // Değişim yönünü hesapla
        const chg = history.length > 1
            ? history[history.length - 1] / history[history.length - 2] - 1
            : 0;
        const dir = chg > +0.000000001
            ? 'up'
            : chg < -0.000000001
                ? 'down'
                : 'none';

        // Veri güncellendiğinde (flare=true) yanıp sönme ekle
        if (flare === true) {
            // Önceki içeriği temizle
            cell.innerHTML = '';

            // Dış konteyner
            const outerContainer = document.createElement('div');
            outerContainer.className = `ticker chg-${dir}`;

            // İç konteyner - animasyon sınıflarıyla
            const innerContainer = document.createElement('div');
            innerContainer.className = 'chg value blink-container price-blink data-update-blink';
            innerContainer.textContent = cellValue;

            // İç konteyneri dış konteynere ekle
            outerContainer.appendChild(innerContainer);
            cell.appendChild(outerContainer);

            // Animasyon tamamlandıktan sonra sınıfı kaldır

            setTimeout(() => {
                innerContainer.classList.remove('data-update-blink', 'chg-up', 'chg-down', 'chg-none');
            }, 500); // 0.5 saniye animasyon süresi
        } else {
            // Standart görünüm - animasyon olmadan ama renk stillemesiyle
            cell.innerHTML = '';

            const outerContainer = document.createElement('div');
            outerContainer.className = `ticker chg-${dir}`;

            const valueContainer = document.createElement('div');
            valueContainer.className = 'chg value';
            valueContainer.textContent = cellValue;

            outerContainer.appendChild(valueContainer);
            cell.appendChild(outerContainer);
        }
    };


    const formatMarketCapCell = (cell, item, col, flare) => {
        // Değeri formatla
        const cellValue = formatValueCell(item, col);

        // Tarihçe verisi yoksa basit metin göster
        if (!item.HistoryRepository) {
            cell.textContent = cellValue;
            return;
        }

        // Bu kolon için geçmiş verilerini al
        const history = item.HistoryRepository[col.binding] || [];
        const chg = history.length > 1
            ? history[history.length - 1] / history[history.length - 2] - 1
            : 0;
        const dir = chg > 0 ? 'up' : chg < 0 ? 'down' : 'none';

        // Hücreyi baştan temizle
        cell.innerHTML = '';

        // Gerçek zamanlı güncelleme varsa yanıp sönme, değilse statik gösterim
        if (flare) {
            // Dış konteyner
            const outer = document.createElement('div');
            outer.className = `ticker chg-${dir}`;

            // İç konteyner ile animasyon sınıfları
            const inner = document.createElement('div');
            inner.className = 'chg value blink-container price-blink data-update-blink';
            inner.textContent = cellValue;

            outer.appendChild(inner);
            cell.appendChild(outer);

            // Animasyon sonrası sınıfı temizle
            setTimeout(() => {
                inner.classList.remove('data-update-blink', 'chg-up', 'chg-down', 'chg-none');
            }, 500);
        } else {
            // Tekrarsız gösterim
            const outer = document.createElement('div');
            outer.className = `ticker chg-${dir}`;
            const valueDiv = document.createElement('div');
            valueDiv.className = 'chg value';
            valueDiv.textContent = cellValue;
            outer.appendChild(valueDiv);
            cell.appendChild(outer);
        }
    };

    // --- Custom formatAskCell, formatDailyHighCell, formatDailyLowCell implementations ---
    const formatAskCell = (cell, item, col, flare) => {
        // Değeri formatla
        const cellValue = formatValueCell(item, col);

        // Tarihçe verisi yoksa temel stili göster
        if (!item.HistoryRepository) {
            cell.textContent = cellValue;
            return;
        }

        // Bu kolon için geçmiş verilerini al
        const history = item.HistoryRepository[col.binding] || [];

        // Değişim yönünü hesapla
        const chg = history.length > 1
            ? history[history.length - 1] / history[history.length - 2] - 1
            : 0;
        const dir = chg > +0.000000001
            ? 'up'
            : chg < -0.000000001
                ? 'down'
                : 'none';

        // Veri güncellendiğinde (flare=true) yanıp sönme ekle
        if (flare) {
            // Önceki içeriği temizle
            cell.innerHTML = '';

            // Dış konteyner
            const outerContainer = document.createElement('div');
            outerContainer.className = `ticker chg-${dir}`;

            // İç konteyner - animasyon sınıflarıyla
            const innerContainer = document.createElement('div');
            innerContainer.className = 'chg value blink-container price-blink data-update-blink';
            innerContainer.textContent = cellValue;

            // İç konteyneri dış konteynere ekle
            outerContainer.appendChild(innerContainer);
            cell.appendChild(outerContainer);

            // Animasyon tamamlandıktan sonra sınıfı kaldır
            setTimeout(() => {
                innerContainer.classList.remove('data-update-blink', 'chg-up', 'chg-down', 'chg-none');
            }, 500);
        } else {
            // Standart görünüm
            cell.innerHTML = '';
            const outerContainer = document.createElement('div');
            outerContainer.className = `ticker chg-${dir}`;
            const valueContainer = document.createElement('div');
            valueContainer.className = 'chg value';
            valueContainer.textContent = cellValue;
            outerContainer.appendChild(valueContainer);
            cell.appendChild(outerContainer);
        }
    };

    const formatDailyHighCell = (cell, item, col, flare) => {
        // Değeri formatla
        const cellValue = formatValueCell(item, col);

        if (!item.HistoryRepository) {
            cell.textContent = cellValue;
            return;
        }

        const history = item.HistoryRepository[col.binding] || [];
        const chg = history.length > 1
            ? history[history.length - 1] / history[history.length - 2] - 1
            : 0;
        const dir = chg > 0
            ? 'up'
            : chg < 0
                ? 'down'
                : 'none';

        if (flare) {
            cell.innerHTML = '';
            const outerContainer = document.createElement('div');
            outerContainer.className = `ticker chg-${dir}`;
            const innerContainer = document.createElement('div');
            innerContainer.className = 'chg value blink-container price-blink data-update-blink';
            innerContainer.textContent = cellValue;
            outerContainer.appendChild(innerContainer);
            cell.appendChild(outerContainer);
            setTimeout(() => {
                innerContainer.classList.remove('data-update-blink');
                cell.textContent = cellValue;
            }, 1000);
        } else {
            cell.innerHTML = '';
            const outerContainer = document.createElement('div');
            outerContainer.className = `ticker chg-${dir}`;
            const valueContainer = document.createElement('div');
            valueContainer.className = 'chg value';
            valueContainer.textContent = cellValue;
            outerContainer.appendChild(valueContainer);
            cell.appendChild(outerContainer);
        }
    };

    const formatDailyLowCell = (cell, item, col, flare) => {
        // Değeri formatla
        const cellValue = formatValueCell(item, col);

        if (!item.HistoryRepository) {
            cell.textContent = cellValue;
            return;
        }

        const history = item.HistoryRepository[col.binding] || [];
        const chg = history.length > 1
            ? history[history.length - 1] / history[history.length - 2] - 1
            : 0;
        const dir = chg > 0
            ? 'up'
            : chg < 0
                ? 'down'
                : 'none';

        if (flare) {
            cell.innerHTML = '';
            const outerContainer = document.createElement('div');
            outerContainer.className = `ticker chg-${dir}`;
            const innerContainer = document.createElement('div');
            innerContainer.className = 'chg value blink-container price-blink data-update-blink';
            innerContainer.textContent = cellValue;
            outerContainer.appendChild(innerContainer);
            cell.appendChild(outerContainer);
            setTimeout(() => {
                innerContainer.classList.remove('data-update-blink');
                cell.textContent = cellValue;
            }, 1000);
        } else {
            cell.innerHTML = '';
            const outerContainer = document.createElement('div');
            outerContainer.className = `ticker chg-${dir}`;
            const valueContainer = document.createElement('div');
            valueContainer.className = 'chg value';
            valueContainer.textContent = cellValue;
            outerContainer.appendChild(valueContainer);
            cell.appendChild(outerContainer);
        }
    };



    const formatIndicatorsCell = (cell, item, col, flare) => {
        const indicatorsSignature = Array.isArray(item.indicators)
            ? item.indicators
                .map(ind => `${ind?.fieldId ?? ind?.id ?? 'x'}:${ind?.score ?? ''}`)
                .join('|')
            : 'none';

        indicatorCellStateRef.current.set(cell, {
            entityId: item.entityId,
            indicatorsRef: item.indicators,
        });

        renderReactCell(cell, col.binding, item.entityId, `indicators-${item.entityId}-${indicatorsSignature}`,
            <div className='roboto-medium-14 taBg'>
                <ScoreFields data={item} />
            </div>
        );
    };

    const formatDailyVolumeCell = (cell, item, col, flare) => {
        const rawValue = item.dailyVolume;
        // Determine direction based on raw numeric values
        const history = item.HistoryRepository && item.HistoryRepository[col.binding] ? item.HistoryRepository[col.binding] : [];
        const prevRaw = history.length > 0 ? Number(history[history.length - 1]) : null;
        const currRaw = Number(rawValue);
        const dir = prevRaw !== null
            ? (currRaw > prevRaw ? 'up' : currRaw < prevRaw ? 'down' : 'none')
            : 'none';
        let displayValue;
        if (rawValue === undefined || rawValue === null || rawValue === '' || isNaN(Number(rawValue))) {
            displayValue = '-';
        } else {
            const numericValue = Number(rawValue) / 1000.0;
            displayValue = numericValue.toLocaleString();
        }
        if (flare) {
            cell.innerHTML = '';
            const outerContainer = document.createElement('div');
            outerContainer.className = `ticker chg-${dir}`;
            const innerContainer = document.createElement('div');
            innerContainer.className = 'chg value blink-container price-blink data-update-blink';
            innerContainer.textContent = displayValue;
            outerContainer.appendChild(innerContainer);
            cell.appendChild(outerContainer);
            setTimeout(() => {
                innerContainer.classList.remove('data-update-blink');
                cell.textContent = displayValue;
            }, 1000);
        } else {
            cell.innerHTML = `<div class="ticker chg-${dir}"><div class="chg value">${displayValue}</div></div>`;
        }
    };

    const formatSignalArrayCell = (cell, item, col) => {
        renderReactCell(cell, col.binding, item.entityId, `signal-${item.entityId}`,
            <div className='roboto-medium-14 taBg'>
                <FontAwesomeIcon className='technicalAnalysisIcon' icon={faChartLine} onClick={() => clickGoTechnicalAnalysisPage(item)} />
            </div>
        );
    };

    const formatDailyValueCell = (cell, item, col, flare) => {
        const rawValue = item.dailyValue;
        // Determine direction based on raw numeric values
        const history = item.HistoryRepository && item.HistoryRepository[col.binding] ? item.HistoryRepository[col.binding] : [];
        const prevRaw = history.length > 0 ? Number(history[history.length - 1]) : null;
        const currRaw = Number(rawValue);
        const dir = prevRaw !== null
            ? (currRaw > prevRaw ? 'up' : currRaw < prevRaw ? 'down' : 'none')
            : 'none';

        let displayValue;
        if (rawValue === undefined || rawValue === null || rawValue === '' || isNaN(Number(rawValue))) {
            displayValue = '-';
        } else {
            const numericValue = Number(rawValue) / 1000000.0;
            const formatted = numberFormatter.Double(numericValue, 2);
            displayValue = (formatted === null || formatted === undefined) ? '-' : formatted;
        }
        if (flare) {
            cell.innerHTML = '';
            const outerContainer = document.createElement('div');
            outerContainer.className = `ticker chg-${dir}`;
            const innerContainer = document.createElement('div');
            innerContainer.className = 'chg value blink-container price-blink data-update-blink';
            innerContainer.textContent = displayValue;
            outerContainer.appendChild(innerContainer);
            cell.appendChild(outerContainer);
            setTimeout(() => {
                innerContainer.classList.remove('data-update-blink');
                cell.textContent = displayValue;
            }, 1000);
        } else {
            cell.innerHTML = `<div class="ticker chg-${dir}"><div class="chg value">${displayValue}</div></div>`;
        }
    };

    const formatTechnicalAnalysisCell = (cell, item, col, flare) => {
        renderReactCell(cell, col.binding, item.entityId, `ta-${item.entityId}`,
            <div className='roboto-medium-14 taBg'>
                <FontAwesomeIcon className='technicalAnalysisIcon' icon={faChartLine} onClick={() => clickGoTechnicalAnalysisPage(item)} />
            </div>
        );
    };

    const formatFavWatchlistCell = (cell, item, col, flare) => {
        renderReactCell(cell, col.binding, item.entityId, `fav-${item.entityId}`,
            <FontAwesomeIcon
                icon={faStar}
                style={{ color: '#EDB618' }}
                onClick={() => removeSymbolOnWatchlist(item)}
            />
        );
    };

    const formatDynamicCell = (cell, item, col, flare) => {
        const cellValue = formatValueCell(item, col);
        // Eğer geçmiş verisi yoksa direkt göster
        if (!item.HistoryRepository) {
            cell.textContent = cellValue;
            return;
        }
        const history = item.HistoryRepository[col.binding] || [];
        const chg = history.length > 1
            ? history[history.length - 1] / history[history.length - 2] - 1
            : 0;
        const dir = chg > 0 ? 'up' : chg < 0 ? 'down' : 'none';

        if (flare) {
            // Blink animasyonu için yapıyı oluştur
            cell.innerHTML = '';
            const outerContainer = document.createElement('div');
            outerContainer.className = `ticker chg-${dir}`;
            const innerContainer = document.createElement('div');
            innerContainer.className = 'chg value blink-container price-blink data-update-blink';
            innerContainer.textContent = cellValue;
            outerContainer.appendChild(innerContainer);
            cell.appendChild(outerContainer);
            // Animasyon sonrası sınıfı kaldır
            setTimeout(() => {
                innerContainer.classList.remove('data-update-blink');
            }, 1000);
        } else {
            // Normal görünüm
            cell.innerHTML = `<div class="ticker chg-${dir}"><div class="chg value">${cellValue}</div></div>`;
        }
    };

    const formatChangeCell = (cell, item, col, flare) => {
        let html = '<div class="ticker flare-{dir}"><div class="chg">{value}</div><div class="glyph"><span class="wj-glyph-{glyph}"></span></div></div>';
        var cellValue = formatValueCell(item, col);
        html = html.replace('{value}', cellValue);
        let glyph = item[col.binding] > 0 ? 'up' : item[col.binding] < 0 ? 'down' : 'circle';
        html = html.replace('{glyph}', glyph);
        let dir = glyph === 'circle' ? 'none' : glyph;
        html = html.replace('{dir}', dir);
        cell.innerHTML = html;
    }

    const formatDailyChangeCell = (cell, item, col, flare) => {
        const raw = item[col.binding];
        const valStr = formatValueCell(item, col);
        const dir = raw > 0 ? 'up' : raw < 0 ? 'down' : 'none';
        const arrow = dir === 'up' ? '▲' : dir === 'down' ? '▼' : '•';
        cell.classList.add('dc-cell');
        const changeClasses = `wl-change ${dir}${flare ? ' data-update-blink' : ''}`;
        cell.innerHTML = `
        <div class="${changeClasses}">
          <span class="icon">${arrow}</span>
          <span class="percent">% ${valStr}</span>
        </div>`;
        if (flare) {
            const blinkContainer = cell.querySelector('.wl-change');
            setTimeout(() => {
                blinkContainer.classList.remove('data-update-blink');
            }, 1000);
        }
    };



    const formatSymbolCell = (cell, item, col, flare) => {
        var uniqueCode = item.code;
        var html = "<a class='securitySelect securityCode'> {uniqueCode}<a/>";
        html = html.replace(/{uniqueCode}/g, uniqueCode);
        cell.innerHTML = html;

        // Only add listener if not already added (tracked via data attribute)
        if (!cell.dataset.hasListener) {
            cell.dataset.hasListener = 'true';
            cell.addEventListener('click', () => {
                window.open(`/StockAnalysis/summary/${item.code}`, '_blank');
            });
        }
    }

    const formatSymbolNameCell = (cell, item, col, flare) => {
        var name = item.name;
        if (name == undefined) {
            name = ' - ';
        }
        var html = `<a class='securitySelect securityCode' style='white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-family: Roboto, sans-serif; font-weight: normal;'>${name}<a/>`;
        cell.innerHTML = html;

        // Only add listener if not already added (tracked via data attribute)
        if (!cell.dataset.hasListener) {
            cell.dataset.hasListener = 'true';
            cell.addEventListener('click', () => {
                window.open(`/StockAnalysis/summary/${item.code}`, '_blank');
            });
        }
    }

    const formatTickerCell = (cell, item, col, flare) => {
        var uniqueCode = item.UniqueCode;
        var html = "<a class='securitySelect securityName'>  {uniqueCode}<a/>";
        html = html.replace(/{uniqueCode}/g, uniqueCode);
        cell.innerHTML = html;

        // Only add listener if not already added (tracked via data attribute)
        if (!cell.dataset.hasListener) {
            cell.dataset.hasListener = 'true';
            cell.addEventListener('click', () => {
                window.open(`/StockAnalysis/summary/${item.code}`, '_blank');
            });
        }
    }

    const formatLastUpdateTimeCell = (cell, item, col, flare) => {
        var html = '';
        try {
            if (item == undefined) return;
            var lut = item['lastUpdateTime'];
            if (lut == undefined) return;
            html = moment.utc(lut, 'X').local().format('HH:mm:ss');
        } catch (e) {
            html = '';
        } finally {
            cell.innerHTML = html;
        }
    }

    const formatLastTradeTimeCell = (cell, item, col, flare) => {
        const value = item[col.binding];
        cell.textContent = (value && value !== 'undefined') ? value : '-';
    };


    const formatValueCell = (item, col) => {
        const originalValue = item[col.binding];

        if (originalValue === undefined || originalValue === null || originalValue === '') {
            return '-';
        }

        const numericValue = Number(originalValue);

        if (isNaN(numericValue)) {
            return '-';
        }

        if (col.binding === 'marketCap') {
            const valueInMln = numericValue / 10000000.0;
            const formatted = numberFormatter.formatNumber(valueInMln, 3);
            return formatted.replace(/\./g, ',');
        }

        let pricePrecision; // Değişkeni burada tanımla
        if (col.binding === 'marketCap') {
            try {
                // Bu özel durum marketCap için değerin kendisini etkiler, pricePrecision'ı değil.
                // Değer formatlanmadan önce bölünecek.
            } catch (e) {
                console.log(e);
            }
        }
        switch (col.binding) {
            case 'dailyVolume':
            case 'lastVolume':
            case 'sharesInIssue':
            case 'marketCap':
                pricePrecision = 0;
                break;
            case 'rocPrevYear':
            case 'rocPrevWeek':
            case 'rocPrevMonth':
            case 'rocEOFPrevYear':
            case 'dailyChange':
                pricePrecision = 2;
                break;
            case '34':
                pricePrecision = 0;
                break;
            case '35':
                pricePrecision = 0;
                break;
            default:
                pricePrecision = item.pricePrecision; // item'dan gelen genel hassasiyet
                break;
        }

        let valueToFormat = numericValue;
        if (col.binding === 'marketCap') {
            valueToFormat = numericValue / 1000000.0;

        }

        // numberFormatter.Double zaten string döndürür.
        const formatted = numberFormatter.Double(valueToFormat, pricePrecision);

        // numberFormatter.Double null/undefined gibi bir değer döndürürse diye ek güvenlik
        return (formatted === null || formatted === undefined || formatted === '') ? '-' : formatted;
    }

    //#endregion Format Cells

    const clickGoTechnicalAnalysisPage = (data) => {
        try {
            if (data.entityId != undefined) {
                // Store selected security in localStorage before navigation
                const selectedSecurity = {
                    id: data.entityId,
                    objectId: data.entityId,
                    code: data.UniqueCode || data.code,
                    name: data.name || data.symbolName
                };
                localStorage.setItem('selectedSecurity', JSON.stringify(selectedSecurity));

                toast.current.show({ severity: 'info', summary: t('dataTable.directedTechnicalAnalsis'), detail: t('pleaceWait'), life: 3000 });
                navigate("/technicalAnalysis");
            } else {
                toast.current.show({ severity: 'error', summary: t('dataTable.notEntityId'), detail: t('dataTable.directedTechnicalAnalsis'), life: 3000 });
            }
        } catch (error) {
            console.log('clickGoTechnicalAnalysisPage error: ', error);
        }
    }

    const openColumnsSelectContainer = () => {
        try {
            setVisibleColumnSelect(!visibleColumnSelect);
            // if (visibleColumnSelect) {
            //    initializeGrid(theGridRef.current.control);
            //}
        } catch (error) {
            console.log('openColumnsSelectContainer error: ', error);
        }
    }

    const restartColumns = () => {
        try {
            const restored = cloneDeep(initialColumnsList);
            setColumnsData(restored);
            setCount(getVisibleCount(restored));


            if (theGridRef.current?.control) {
                const grid = theGridRef.current.control;
                grid.columns.forEach(gCol => {
                    const src = restored.find(c => c.binding === gCol.binding);
                    if (src) gCol.visible = src.isVisible;
                });
            }


            const dynamic = restored.filter(c =>
                !fixedColumns.some(f => f.binding === c.binding)
            );
            persistOrderLocally(dynamic.map(col => col.binding));
            isLiveCategory()
                ? syncRealtimeColumnsField(dynamic)
                : syncColumnsField(dynamic);

        } catch (err) {
            console.log('restartColumns', err);
        }
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;

        const items = Array.from(columnsData.filter(col => !fixedColumns.some(fc => fc.binding === col.binding)));
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        // Order numaralarını güncelle
        const updatedItems = items.map((item, index) => ({ ...item, order: index + 1 }));

        const updatedColumns = [...fixedColumns, ...updatedItems];
        setColumnsData(updatedColumns);
        setColumns(updatedItems);
        setInitialColumnsList(cloneDeep(updatedColumns));
        persistOrderLocally(updatedItems.map(col => col.binding));

        const dynamicColumns = updatedItems;

        if (isLiveCategory()) {
            syncRealtimeColumnsField(dynamicColumns);
        } else {
            syncColumnsField(dynamicColumns);
        }
    };

    const onClickAddColumnItem = (col) => {
        if (count >= 13) {
            toast.current.show({
                severity: 'error',
                summary: 'Hata',
                detail: 'Eklenebilecek Max Sütun Sınırına Ulaşılmıştır!'
            });
            return;
        }

        // 1 | immutabl güncelle
        const updated = columnsData.map(c =>
            c.binding === col.binding ? { ...c, isVisible: true } : c
        );
        setColumnsData(updated);
        setCount(getVisibleCount(updated));

        // 2 | grid görünürlüğü
        if (theGridRef.current?.control) {
            const grid = theGridRef.current.control;
            const gCol = grid.columns.getColumn(col.binding);
            if (gCol) gCol.visible = true;
        }

        // 3 | sunucuya sync
        const dynamic = updated.filter(c =>
            !fixedColumns.some(f => f.binding === c.binding)
        );
        isLiveCategory()
            ? syncRealtimeColumnsField(dynamic)
            : syncColumnsField(dynamic);
    };




    const onClickRemoveColumnItem = (item) => {
        item.isVisible = false;
        item.defaultVisible = false;

        // Kolonları güncelle
        const updatedColumns = columns.filter((column) => column.binding !== item.binding);
        setColumns(updatedColumns);

        // columnsData'yı güncelle
        const updatedColumnsData = columnsData.map(col => {
            if (col.binding === item.binding) {
                return { ...col, isVisible: false, defaultVisible: false };
            }
            return col;
        });
        setColumnsData(updatedColumnsData);

        // FlexGrid kolon görünürlüğünü güncelle
        if (theGridRef.current && theGridRef.current.control) {
            const grid = theGridRef.current.control;
            grid.columns.forEach((col) => {
                if (col.binding === item.binding) {
                    col.visible = false;
                }
            });
        }

        // columnsList içindeki görünür kolonların sayısını güncelle ve +3 ekle
        // setCount(columnsList.filter(col => col.isVisible).length + 3);
        //setCount(count - 1);
        setCount(getVisibleCount(updatedColumnsData));
        // Sadece dinamik kolonları alarak syncColumnsField fonksiyonunu çağırıyoruz
        const dynamicColumns = updatedColumnsData.filter(col => !fixedColumns.some(fc => fc.binding === col.binding));
        if (isLiveCategory()) {
            syncRealtimeColumnsField(dynamicColumns);
        } else {
            syncColumnsField(dynamicColumns);
        }
    };




    const syncColumnsField = (dynamicColumns) => {

        const grid = theGridRef.current.control;
        let columnsToSync;

        if (dynamicColumns == null) {
            columnsToSync = grid.columns.filter(col => !fixedColumns.some(fc => fc.binding === col.binding)).map(col => ({
                id: Number(col.binding),
                enable: col.visible,
                order: col.index,
                width: col.width === "*" ? 0 : Number(col.width),
            }));
        }

        else {
            columnsToSync = dynamicColumns.filter(col => !fixedColumns.some(fc => fc.binding === col.binding)).map(col => ({
                id: Number(col.binding),
                enable: col.isVisible,
                order: col.order,
                width: col.width === "*" ? 0 : Number(col.width),
            }));
        }

        let syncColumnsField = {
            id: selectedListBarItem,
            category: mapCategoryBinding(activeCategory?.binding),
            Fields: columnsToSync
        };

        // Sunucuya POST isteği gönderiyoruz
        watchlistApi.watchlistSyncFieldsPost(token, syncColumnsField)
            .then((res) => {
                if (!res.data.error) {
                    // toast.current.show({
                    //     severity: 'success',
                    //     summary: 'Başarılı',
                    //     detail: 'Kolon sıralaması başarıyla kaydedildi.',
                    //     life: 3000,
                    // });
                } else {
                    // toast.current.show({
                    //     severity: 'error',
                    //     summary: 'Hata',
                    //     detail: res.data.error.message,
                    //     life: 3000,
                    // });
                }
            })
            .catch((err) => {
                console.log(err);
                // toast.current.show({
                //     severity: 'error',
                //     summary: 'Hata',
                //     detail: 'Kolon sıralaması kaydedilemedi.',
                //     life: 3000,
                // });
            });
    };

    const syncRealtimeColumnsField = (dynamicColumns) => {


        const grid = theGridRef.current.control;
        let columnsToSync;

        if (dynamicColumns == null) {
            columnsToSync = grid.columns.filter(col => !fixedColumns.some(fc => fc.binding === col.binding)).map(col => ({
                code: col.binding,
                enable: col.visible,
                order: col.index,
                width: col.width === "*" ? 0 : Number(col.width),
            }));
        }
        else {
            columnsToSync = dynamicColumns.filter(col => !fixedColumns.some(fc => fc.binding === col.binding)).map(col => ({
                code: col.binding,
                enable: col.isVisible,
                order: col.order,
                width: col.width === "*" ? 0 : Number(col.width),
            }));
        }

        let syncColumnsField = {
            id: selectedListBarItem,
            RealtimeFields: columnsToSync
        };

        // Sunucuya POST isteği gönderiyoruz
        watchlistApi.realtimeSyncRealtimeFieldsPost(token, syncColumnsField)
            .then((res) => {
                if (!res.data.error) {
                    // toast.current.show({
                    //     severity: 'success',
                    //     summary: 'Başarılı',
                    //     detail: 'Kolon sıralaması başarıyla kaydedildi.',
                    //     life: 3000,
                    // });
                    console.log("syncRealtimeColumnsField: Kolon sıralaması başarıyla kaydedildi.", res);
                } else {
                    // toast.current.show({
                    //     severity: 'error',
                    //     summary: 'Hata',
                    //     detail: res.data.error.message,
                    //     life: 3000,
                    // });
                    console.log("syncRealtimeColumnsField: Kolon sıralaması kaydedilemedi.", res);
                }
            })
            .catch((err) => {
                console.log(err);


            });
    };

    //#region Add Search/Watchlist Functions
    const selectedSearchItem = (item) => {
        addSymbolToWatchlist(item);
    }

    const addSymbolToWatchlist = (item) => {
        const id = 0;
        watchlistApi.watchlistAddSymbolGet(token, selectedListBarItem, item.document.objectId).then((res) => {
            console.log("addItem: ", res);
            if (!res.data.error) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Başarılı',
                    detail: "'" + item.document.code + "' izleme listesine eklendi.",
                    life: 3000,
                });
                dispatch(setUpdatedList({ selectedListBarItem: selectedListBarItem, updatedList: res.data.watchlistSymbols }));
                setStreamLoading(false);
            } else {
                if (res.data.error.includes("already exists")) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Hata',
                        detail: "'" + item.document.code + "' izleme listesinde zaten mevcut.",
                        life: 3000,
                    });
                }
                else {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Bir hata oluştu (addSymbolToWatchlist) : ' + res.data.error,
                        detail: res.data.error.message,
                        life: 3000,
                    });
                }
            }
        }).catch((err) => {
            console.log(err);
        });
    }

    //#endregion Add Search/Watchlist Functions
    return (
        <Card className="grid-card watchlist-table" style={{ width: "100%", height: "auto" }}>
            <div className=''>
                <div className='watchlist-table-inner-content'>
                    <Toast ref={toast} position="bottom-right" />
                    <Container className='p-t-0'>
                        <Row>
                            <Col md='4' sm='7'>
                                <div className='search-input search-container-classname'>
                                    <Search selectedSearchItem={selectedSearchItem} disabled={isSearchDisabled} />
                                </div>
                            </Col>
                            <Col md='8' sm='5' className='float-right'>
                                <Button className='columnsSelectButton buttonCustom' onClick={() => openColumnsSelectContainer()}>
                                    <FontAwesomeIcon className='text-align-center' icon={faGear} />
                                </Button>
                            </Col>
                        </Row>
                    </Container>
                    {visibleColumnSelect && (
                        <Container className='columnsSelectContainer'>
                            <Row className='columnsSelectHeaderRow'>
                                <Col md="11" sm='11' className='color-text roboto-medium-14 p-l-20'>{t('editColumns')}</Col>
                                <Col md="1" sm="1" onClick={restartColumns}>
                                    <FontAwesomeIcon className='text-align-right float-right p-r-20' style={{ cursor: 'pointer' }} icon={faArrowRotateRight} />
                                </Col>
                            </Row>
                            <Container className='p-l-10 p-r-10'>
                                <Row>
                                    <Col md='6' sm='12'>
                                        <Row>
                                            <Col md="12" sm='12' className='roboto-bold-14 p-b-15'>{t('columnsToAdd')}</Col>
                                        </Row>
                                        <Row className='p-l-10 p-r-10 scolling-row'>
                                            {columnsData.map(item => (
                                                <>
                                                    {!item.isVisible && item.header !== "" && !["UniqueCode", "signalArray", "favWatchlist", "name", "technicalAnalysis", "indicators"].includes(item.binding) && (
                                                        <Col key={item.binding} md='12' sm="12" className='columnsListItem m-b-10 p-l-10 p-r-10' onClick={() => onClickAddColumnItem(item)}>
                                                            <FontAwesomeIcon className='text-align-center float-left listItemIcon' icon={faSquareChevronRight} />
                                                            <span className='p-l-10 roboto-medium-14'>
                                                                {item.header}
                                                                {(item.info) && (
                                                                    <>
                                                                        <FontAwesomeIcon
                                                                            className='info-icon'
                                                                            icon={faInfoCircle}
                                                                            data-pr-tooltip={item.info}
                                                                            data-pr-position="left"
                                                                            data-pr-at="right+5 top"
                                                                            data-pr-my="left center-2"
                                                                            tooltipOptions={{ position: 'mouse' }}
                                                                        />
                                                                        <Tooltip target=".info-icon" />
                                                                    </>
                                                                )}
                                                            </span>
                                                        </Col>
                                                    )}
                                                </>
                                            ))}
                                        </Row>
                                    </Col>
                                    <Col md='6' sm='12'>
                                        <Row>
                                            <Col md="10" sm='10' className='roboto-bold-14 p-b-15'>{t('columnSelection')}</Col>
                                            <Col md="2" sm='2' className='numberOfSelectedColums roboto-bold-14'>
                                                {count}/13
                                            </Col>
                                        </Row>
                                        <DragDropContext onDragEnd={onDragEnd}>
                                            <Droppable droppableId="columns">
                                                {(provided) => (
                                                    <Row className='p-l-10 p-r-10 scolling-row' {...provided.droppableProps} ref={provided.innerRef}>
                                                        {fixedColumns.filter(item => item.binding !== 'favWatchlist' && item.binding !== 'technicalAnalysis').map((item, index) => (
                                                            <Col key={item.binding} md='12' className='columnsSelectedListItem m-b-10'>
                                                                <span className='p-l-10 roboto-medium-14'>{item.header}</span>
                                                            </Col>
                                                        ))}
                                                        {columnsData.filter(item => !fixedColumns.some(fc => fc.binding === item.binding)).map((item, index) => (
                                                            <>
                                                                {item.isVisible && item.header !== "" && (
                                                                    <Draggable key={item.binding} draggableId={item.binding} index={index}>
                                                                        {(provided) => (
                                                                            <Col
                                                                                md='12'
                                                                                className='columnsSelectedListItem m-b-10'
                                                                                ref={provided.innerRef}
                                                                                {...provided.draggableProps}
                                                                                {...provided.dragHandleProps}
                                                                            >
                                                                                <FontAwesomeIcon
                                                                                    className='text-align-center float-left listItemIcon'
                                                                                    icon={faSquareCheck}
                                                                                    onClick={() => onClickRemoveColumnItem(item)}
                                                                                />
                                                                                <label className='p-l-10 roboto-medium-14'  >
                                                                                    {item.header}
                                                                                    {item.info && (
                                                                                        <>
                                                                                            <FontAwesomeIcon
                                                                                                className='info-icon'
                                                                                                icon={faInfoCircle}
                                                                                                data-pr-tooltip={item.info}
                                                                                                data-pr-position="left"
                                                                                                data-pr-at="right+5 top"
                                                                                                data-pr-my="left center-2"
                                                                                                tooltipOptions={{ position: 'mouse' }}
                                                                                            />
                                                                                            <Tooltip target=".info-icon" style={{ fontSize: '11px' }} />
                                                                                        </>
                                                                                    )}
                                                                                </label>
                                                                                <FontAwesomeIcon style={{ textAlign: 'right', float: 'right' }} className='text-align-right float-right listItemIcon' icon={faGripDotsVertical} />
                                                                            </Col>
                                                                        )}
                                                                    </Draggable>
                                                                )}
                                                            </>
                                                        ))}
                                                        {provided.placeholder}
                                                    </Row>
                                                )}
                                            </Droppable>
                                        </DragDropContext>
                                    </Col>
                                </Row>
                            </Container>
                        </Container>
                    )}
                    <div
                        className="table-div"
                        style={{
                            position: 'relative',
                            minHeight: '450px',
                            overflow: 'auto',
                            minWidth: '100%',
                            perspective: '1000px'
                        }}
                    >
                        {/* Premium Perde Efekti (Glassmorphism & Soft Fade) */}
                        <div className="loading-overlay" style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            paddingTop: '80px',
                            position: 'absolute',
                            top: 0, left: 0, right: 0, bottom: 0,
                            zIndex: 10,
                            backgroundColor: theme === 'dark' ? '#2c2c2c' : 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(8px)',
                            WebkitBackdropFilter: 'blur(8px)',
                            opacity: streamLoading ? 1 : 0,
                            visibility: streamLoading ? 'visible' : 'hidden',
                            transition: 'opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1), visibility 0.6s',
                            pointerEvents: streamLoading ? 'auto' : 'none'
                        }}>
                            <div style={{
                                transform: streamLoading ? 'scale(1)' : 'scale(0.8)',
                                opacity: streamLoading ? 1 : 0,
                                transition: 'all 0.6s cubic-bezier(0.22, 1, 0.36, 1)'
                            }}>
                                <Spinner />
                            </div>
                        </div>

                        {/* Tablo İçeriği (Slide-up & Fade-in) */}
                        <div style={{ 
                            opacity: streamLoading ? 0.01 : 1, 
                            transform: streamLoading ? 'translateY(16px)' : 'translateY(0px)',
                            transition: 'opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1), transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)',
                            height: '100%'
                        }}>
                            {!emptyList && gridRows.length > 0 ? (
                                <FlexGrid
                                    ref={theGridRef}
                                className="liveQuotesGrid"
                                allowDelete={false}
                                allowAddNew={false}
                                allowDragging="Columns"
                                allowMerging={0}
                                headersVisibility={"Column"}
                                isReadOnly={true}
                                selectionMode="Row"
                                autoGenerateColumns={false}
                                virtualizationThreshold={300}
                                initialized={(grid) => {
                                    initializeGrid(grid);
                                    gridInitializedRef.current = true;
                                }}
                                itemsSource={gridRows}
                                style={{ height: '100%' }}
                                frozenColumns={5}
                            >
                            {columnsData && columnsData.map((col) => (
                                    <FlexGridColumn
                                        key={col.binding}
                                        binding={col.binding}
                                        header={col.header}
                                        width={col.width}
                                        format={col.format}
                                        align={col.align}
                                        visible={col.isVisible}
                                        cssClass={col.cssClass}
                                    />
                                ))}
                            </FlexGrid>
                        ) : (
                            !streamLoading && emptyList && (
                                <div className="empty-list">
                                    <div className="empty-list-icon">
                                        <FontAwesomeIcon icon={faSearch} className="icon" />
                                    </div>
                                    <p className="empty-list-text">{t('watchList.emptyListMessage')}</p>
                                </div>
                            )
                        )}
                        </div>
                    </div>
                </div>
            </div>
        </Card>
    );


}

const areWatchlistTablePropsEqual = (prevProps, nextProps) => {
    return prevProps.symbolListData === nextProps.symbolListData
        && prevProps.removeSymbolOnWatchlist === nextProps.removeSymbolOnWatchlist
        && prevProps.columnsList === nextProps.columnsList
        && prevProps.tableData === nextProps.tableData
        && prevProps.removedEntityId === nextProps.removedEntityId
        && prevProps.selectedListBarItem === nextProps.selectedListBarItem
        && prevProps.staticLiveData === nextProps.staticLiveData
        && prevProps.activeCategory === nextProps.activeCategory
        && prevProps.onItemsFiltered === nextProps.onItemsFiltered;
};

export default React.memo(WatchlistTable, areWatchlistTablePropsEqual);
